import React, { useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { IntlMessage } from '@rovo-extension/i18n';
import { getBrowserInfo } from '@atlaskit/editor-common/browser';
import Button from '@atlaskit/button/default/button';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import Image from '@atlaskit/image/image';
import Link from '@atlaskit/link/link';
import RefreshIcon from '@atlaskit/icon/core/refresh';
import { RovoIcon } from '@atlaskit/logo/rovo/icon';
import Lozenge from '@atlaskit/lozenge/lozenge';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';
import siteSettingsIllustration from '../../assets/microphone-permission/site-settings-light.svg';
import { postMessageToParent } from '../../message-bus/postMessageToParent';
import { isEdge } from '../../utils/browser';
import { type MicrophonePermissionState, useMicrophonePermission } from './useMicrophonePermission';

type MessageKey =
	| 'heading'
	| 'description'
	| 'statusLabel'
	| 'statusUnknown'
	| 'statusChecking'
	| 'statusAllowed'
	| 'statusBlocked'
	| 'blockedHint'
	| 'allowedHint'
	| 'retryButton'
	| 'closeButton'
	| 'opensNewWindowLabel'
	| 'siteSettingsIllustrationAlt';

const messages: Record<MessageKey, IntlMessage> = defineMessages({
	heading: {
		id: 'rovo-extension.microphone-permission.heading',
		defaultMessage: 'Allow microphone access',
		description:
			'Heading of the page that asks the browser for microphone access for voice dictation.',
	},
	description: {
		id: 'rovo-extension.microphone-permission.description',
		defaultMessage:
			'Voice dictation needs your microphone. Your browser will ask for permission. Choose Allow, then return to the side panel.',
		description: 'Explains why the browser is asking for microphone access.',
	},
	statusLabel: {
		id: 'rovo-extension.microphone-permission.status-label',
		defaultMessage: 'Microphone status',
		description: 'Label in front of the current microphone permission status.',
	},
	statusUnknown: {
		id: 'rovo-extension.microphone-permission.status.unknown',
		defaultMessage: 'Unknown',
		description: 'Status shown before microphone permission has been checked.',
	},
	statusChecking: {
		id: 'rovo-extension.microphone-permission.status.checking',
		defaultMessage: 'Checking',
		description: 'Status shown while the browser is asking for microphone permission.',
	},
	statusAllowed: {
		id: 'rovo-extension.microphone-permission.status.allowed',
		defaultMessage: 'Allowed',
		description: 'Status shown when microphone permission has been granted.',
	},
	statusBlocked: {
		id: 'rovo-extension.microphone-permission.status.blocked',
		defaultMessage: 'Blocked',
		description: 'Status shown when microphone permission was denied or the prompt was dismissed.',
	},
	blockedHint: {
		id: 'rovo-extension.microphone-permission.blocked-hint',
		defaultMessage:
			"Microphone access wasn't granted. If you missed the prompt, try again. If it doesn't appear, allow the microphone for this extension in your browser's <link>site settings</link>.",
		description:
			"Guidance shown when microphone permission is blocked. The link tag wraps text that becomes a link to Google Chrome's microphone permission help page, and stays plain text in every other browser.",
	},
	allowedHint: {
		id: 'rovo-extension.microphone-permission.allowed-hint',
		defaultMessage:
			'Microphone access is on. This tab closes in a moment, and you can start dictating in the side panel.',
		description:
			'Guidance shown once microphone permission has been granted, before the tab closes itself.',
	},
	retryButton: {
		id: 'rovo-extension.microphone-permission.retry-button',
		defaultMessage: 'Try again',
		description: 'Button that asks the browser for microphone permission again.',
	},
	opensNewWindowLabel: {
		id: 'rovo-extension.microphone-permission.opens-new-window-label',
		defaultMessage: '(opens new window)',
		description:
			'Label announced by screen readers after the site settings link, because the link opens in a new browser tab.',
	},
	siteSettingsIllustrationAlt: {
		id: 'rovo-extension.microphone-permission.site-settings-illustration-alt',
		defaultMessage: 'A browser address bar with a crossed-out microphone icon on its left.',
		description:
			'Alternative text for the illustration that shows where the blocked microphone icon appears in the browser address bar.',
	},
	closeButton: {
		id: 'rovo-extension.microphone-permission.close-button',
		defaultMessage: 'Close tab',
		description: 'Button that closes the microphone permission tab.',
	},
});

// Layout and motion mirror the shell fallback page (`shell-fallback/render.ts`).
const styles = cssMap({
	page: {
		minHeight: '100vh',
		boxSizing: 'border-box',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		paddingTop: token('space.300'),
		paddingRight: token('space.300'),
		paddingBottom: token('space.300'),
		paddingLeft: token('space.300'),
		backgroundColor: token('elevation.surface'),
		color: token('color.text'),
	},
	content: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		width: '100%',
		maxWidth: '400px',
		textAlign: 'center',
	},
	logo: {
		position: 'relative',
		display: 'grid',
		placeItems: 'center',
		width: '96px',
		height: '96px',
	},
	glow: {
		position: 'absolute',
		insetBlock: token('space.100'),
		insetInline: token('space.100'),
		borderRadius: token('radius.full'),
	},
	logoIcon: {
		position: 'relative',
		display: 'flex',
	},
	illustration: {
		width: '100%',
		overflow: 'hidden',
		borderRadius: token('radius.large'),
	},
});

// Defined only when motion is welcome, so a reduced-motion user gets static elements.
const KEYFRAMES = `
@media (prefers-reduced-motion: no-preference) {
	@keyframes microphone-permission-enter {
		from { opacity: 0; transform: translateY(8px); }
		to { opacity: 1; transform: none; }
	}
	@keyframes microphone-permission-spin {
		to { transform: rotate(1turn); }
	}
	@keyframes microphone-permission-float {
		50% { transform: translateY(-4px); }
	}
}
`;

// The Rovo brand colors have no design tokens, and blur and opacity are not token-typed.
const GLOW_STYLE = {
	backgroundImage: 'conic-gradient(from 0deg, #1868DB, #AF59E1, #FCA700, #6A9A23, #1868DB)',
	filter: 'blur(24px)',
	opacity: 0.15,
	animation: 'microphone-permission-spin 12s linear infinite',
} as const;

const ENTER_ANIMATION = 'microphone-permission-enter 400ms cubic-bezier(0.32, 0, 0.16, 1) both';
const FLOAT_ANIMATION = 'microphone-permission-float 6s ease-in-out infinite';

const AUTO_CLOSE_DELAY_MS = 1500;

// Referenced from ai-mate voice dictation footer when permission is blocked and tip shown.
const CHROME_MIC_PERMISSION_HELP_URL = 'https://support.google.com/chrome/answer/2693767';
const isChrome = (): boolean => Boolean(getBrowserInfo().chrome) && !isEdge();

const STATUS_MESSAGES = {
	unknown: messages.statusUnknown,
	checking: messages.statusChecking,
	allowed: messages.statusAllowed,
	blocked: messages.statusBlocked,
} as const;

const STATUS_APPEARANCE = {
	unknown: 'default',
	checking: 'inprogress',
	allowed: 'success',
	blocked: 'removed',
} as const satisfies Record<
	MicrophonePermissionState,
	'default' | 'inprogress' | 'success' | 'removed'
>;

export const MicrophonePermissionPage = (): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { state, request } = useMicrophonePermission();

	// Closes this tab once the user has had a moment to see the confirmation. The side panel needs
	// no message: its mic stays enabled while blocked, so the next click checks access again.
	useEffect(() => {
		if (state !== 'allowed') {
			return undefined;
		}

		const timeoutId = window.setTimeout(() => {
			postMessageToParent({ type: 'CLOSE_MICROPHONE_PERMISSION_PAGE' });
		}, AUTO_CLOSE_DELAY_MS);

		return (): void => window.clearTimeout(timeoutId);
	}, [state]);

	return (
		<Box xcss={styles.page}>
			<style>{KEYFRAMES}</style>
			<Box style={{ animation: ENTER_ANIMATION }} xcss={styles.content}>
				<Stack alignInline="center" space="space.300">
					<Box xcss={styles.logo}>
						<Box style={GLOW_STYLE} xcss={styles.glow} />
						<Box style={{ animation: FLOAT_ANIMATION }} xcss={styles.logoIcon}>
							<RovoIcon label="" size="xlarge" />
						</Box>
					</Box>
					<Stack alignInline="center" space="space.100">
						<Heading as="h1" size="large">
							{formatMessage(messages.heading)}
						</Heading>
						<Text as="p" color="color.text.subtle">
							{formatMessage(messages.description)}
						</Text>
					</Stack>
					<Inline alignBlock="center" space="space.100">
						<Text color="color.text.subtle">{formatMessage(messages.statusLabel)}</Text>
						<span aria-live="polite" role="status">
							<Lozenge appearance={STATUS_APPEARANCE[state]} testId="microphone-permission-status">
								{formatMessage(STATUS_MESSAGES[state])}
							</Lozenge>
						</span>
					</Inline>
					{state === 'blocked' && (
						<Text as="p" color="color.text.subtle">
							{formatMessage(messages.blockedHint, {
								link: (chunks: React.ReactNode[]) =>
									isChrome() ? (
										<Link
											href={CHROME_MIC_PERMISSION_HELP_URL}
											newWindowLabel={formatMessage(messages.opensNewWindowLabel)}
											// Keeps the tenant hostname out of the `Referer` sent to Google.
											rel="noreferrer"
											target="_blank"
											testId="microphone-permission-settings-link"
										>
											{chunks}
										</Link>
									) : (
										<React.Fragment>{chunks}</React.Fragment>
									),
							})}
						</Text>
					)}
					{state === 'allowed' ? (
						<>
							<Text as="p" color="color.text.subtle">
								{formatMessage(messages.allowedHint)}
							</Text>
							<Button
								appearance="primary"
								onClick={() => postMessageToParent({ type: 'CLOSE_MICROPHONE_PERMISSION_PAGE' })}
								testId="microphone-permission-close"
							>
								{formatMessage(messages.closeButton)}
							</Button>
						</>
					) : (
						<>
							<Button
								appearance="primary"
								iconBefore={RefreshIcon}
								isDisabled={state === 'checking'}
								onClick={() => void request()}
								testId="microphone-permission-retry"
							>
								{formatMessage(messages.retryButton)}
							</Button>
							<Box xcss={styles.illustration}>
								<Image
									alt={formatMessage(messages.siteSettingsIllustrationAlt)}
									src={siteSettingsIllustration}
									testId="microphone-permission-illustration"
								/>
							</Box>
						</>
					)}
				</Stack>
			</Box>
		</Box>
	);
};
